import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, Lock, Loader2, TimerReset } from 'lucide-react';

interface AdminLoginProps {
  onValidate: (password: string) => Promise<boolean>;
  onSuccess: () => void;
}

const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 60_000;
const LOCKOUT_KEY = 'adminLockout';

interface LockoutState {
  count: number;
  lockedUntil: number | null;
}

const readLockout = (): LockoutState => {
  try {
    const raw = localStorage.getItem(LOCKOUT_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return { count: 0, lockedUntil: null };
};

const writeLockout = (state: LockoutState) => {
  localStorage.setItem(LOCKOUT_KEY, JSON.stringify(state));
};

const AdminLogin: React.FC<AdminLoginProps> = ({ onValidate, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const validatingRef = useRef(false);

  useEffect(() => {
    const state = readLockout();
    if (state.lockedUntil && state.lockedUntil > Date.now()) {
      setLockedUntil(state.lockedUntil);
    }
  }, []);

  useEffect(() => {
    if (!lockedUntil) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        setLockedUntil(null);
        writeLockout({ count: 0, lockedUntil: null });
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || validatingRef.current || lockedUntil) return;

    validatingRef.current = true;
    setIsValidating(true);
    const isValid = await onValidate(password);
    setIsValidating(false);
    validatingRef.current = false;

    if (isValid) {
      writeLockout({ count: 0, lockedUntil: null });
      onSuccess();
    } else {
      const current = readLockout();
      const nextCount = current.count + 1;
      if (nextCount >= MAX_ATTEMPTS) {
        const until = Date.now() + LOCKOUT_MS;
        writeLockout({ count: nextCount, lockedUntil: until });
        setLockedUntil(until);
      } else {
        writeLockout({ count: nextCount, lockedUntil: null });
      }
      setError(true);
      setPassword('');
    }
  };

  const isLocked = !!lockedUntil;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 p-4">
      <form onSubmit={handleSubmit} className="flex flex-col items-center w-full max-w-xs">
        <div className="flex items-center gap-2 mb-8">
          <ShieldCheck className="w-6 h-6 text-cyan-400" />
          <span className="font-bold text-lg text-slate-100">SensorLink Admin</span>
        </div>

        <div className="mb-8 p-4 bg-slate-800 rounded-full border border-slate-700 shadow-xl shadow-cyan-900/20">
          {isLocked ? (
            <TimerReset className="w-8 h-8 text-orange-400" />
          ) : isValidating ? (
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          ) : (
            <Lock className={`w-8 h-8 ${error ? 'text-red-500' : 'text-cyan-400'}`} />
          )}
        </div>

        <h2 className="text-xl font-semibold text-slate-100 mb-2">Internal Access</h2>

        {isLocked ? (
          <p className="text-slate-400 text-sm mb-6 text-center">
            Too many attempts. Try again in <span className="text-orange-400 font-semibold">{secondsLeft}s</span>.
          </p>
        ) : (
          <p className="text-slate-400 text-sm mb-6 text-center">
            Enter the admin password.
          </p>
        )}

        <input
          type="password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(false); }}
          disabled={isValidating || isLocked}
          autoFocus
          className={`w-full mb-4 px-4 py-3 rounded-xl bg-slate-800 border text-slate-100 text-center tracking-widest placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 disabled:opacity-40 ${
            error ? 'border-red-500' : 'border-slate-700'
          }`}
          placeholder="Password"
        />

        <button
          type="submit"
          disabled={isValidating || isLocked || !password}
          className="w-full py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 active:bg-cyan-700 text-white font-medium transition-colors disabled:opacity-40 disabled:hover:bg-cyan-600"
        >
          {isValidating ? 'Checking…' : 'Unlock'}
        </button>
      </form>
    </div>
  );
};

export default AdminLogin;
